import api from "../../utils/api";

export const OFFER_STATUS = {
  DRAFT: "draft",
  ACTIVE: "active",
  PAID: "paid",
  EXPIRED: "expired",
  DISABLED: "disabled",
  REPLACED: "replaced",
  CANCELED: "canceled",
};

/** "1.499 €" / "1.499,50 €" (locale-aware, cents only when needed). */
export function formatOfferPrice(price, locale = "de") {
  const n = Number(price);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatOfferDate(iso, locale = "de", withTime = false) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export function normalizeOffer(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    reference: raw.reference || "",
    status: raw.status || OFFER_STATUS.DRAFT,
    title: raw.title || "",
    description: raw.description || "",
    price: raw.price ?? "0",
    currency: raw.currency || "EUR",
    billingInterval: raw.billing_interval || "monthly",
    features: Array.isArray(raw.features)
      ? raw.features.map((f) => ({ label: f.label || "", included: f.included !== false }))
      : [],
    limits: Array.isArray(raw.limits)
      ? raw.limits.map((l) => ({ label: l.label || "", value: l.value ?? "" }))
      : [],
    extra: raw.extra || {},
    validUntil: raw.valid_until || null,
    version: raw.version ?? 1,
    activatedAt: raw.activated_at || null,
    paidAt: raw.paid_at || null,
    canceledAt: raw.canceled_at || null,
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
    isExpired: Boolean(raw.is_expired),
    canPurchase: Boolean(raw.can_purchase),
    // admin-only fields (absent for customers)
    adminNotes: raw.admin_notes ?? "",
    createdByName: raw.created_by_name || "",
    stripeSubscriptionId: raw.stripe_subscription_id || "",
    customer: raw.customer
      ? {
          id: raw.customer.id,
          fullName: raw.customer.full_name || "",
          email: raw.customer.email || "",
          companyName: raw.customer.company_name || "",
          accountStatus: raw.customer.account_status || "trial",
        }
      : null,
  };
}

/** Customer: the personalised package for this account (null → standard Enterprise card). */
export async function fetchCurrentOffer() {
  const data = await api.get("/api/offers/current/", { auth: true });
  return {
    offer: normalizeOffer(data?.offer),
    isAccountOwner: Boolean(data?.is_account_owner),
    accountStatus: data?.account_status || "trial",
    paymentsAvailable: Boolean(data?.payments_available),
  };
}

/** Customer: start Stripe Checkout for the own offer → {checkoutUrl}. */
export async function startOfferCheckout(offerId) {
  const data = await api.post(`/api/offers/${offerId}/checkout/`, {}, { auth: true });
  return { checkoutUrl: data?.checkout_url || "" };
}
